import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { CREATE_LOT_TOUR, StepHeading, Tour } from '../components/Help';
import {
  ApiError,
  api,
  apiRaw,
  categoryText,
  enc,
  estimatedCost,
  isBinCode,
  productTitle,
  webImageUrl,
  type Lot,
  type MainCategory,
  type Product,
  type WebPage,
} from '../api';
import { ScanField } from '../components/ScanField';
import { ProductPicker } from '../components/ProductPicker';
import { PendingPhotos, type PendingPhoto } from '../components/Photos';
import { ErrorBox, Field, Loading, Modal, Money, SOURCE_LABELS, Success, Thumb, Val, locationText } from '../components/ui';
import { clearStored, useBins, useConditions, useLocations, useSettings, useStoredState } from '../hooks';
import { COMPLETE, CompletenessFields, completenessBody, completenessProblem, type CompletenessDraft } from '../components/Completeness';

const DRAFT_KEY = 'mal.lotDraft.v1';

interface Draft {
  clientRequestId: string;
  lotNumber: string | null;
  product: Product | null;
  unlinkedBarcode: string | null;
  title: string;
  referencePrice: string;
  referenceCurrency: string;
  startingBid: string;
  reservePrice: string;
  bidCurrency: string;
  conditionId: string;
  colour: string;
  sizeModel: string;
  details: string;
  /** Costco description for the lot (separate from the inspection notes in `details`). */
  costcoDescription?: string;
  /** The Costco page the description was pre-filled from (lets the server record the source). */
  costcoPageId?: string | null;
  /** The Costco page the title was pre-filled from. */
  titlePageId?: string | null;
  /** What the Costco website shows for this item, to confirm the right product was picked. */
  costco?: { title: string | null; sourceName: string; url: string; imageId: string | null } | null;
  /** Put the Costco photo first in the lot's photos. */
  useCostcoPhoto?: boolean;
  /** 'mixed': pieces from one or more products, attached to no product. Missing in older drafts = 'product'. */
  kind?: 'product' | 'mixed';
  /** Single product: is everything there? */
  completeness?: CompletenessDraft;
  /** Mixed lot: the pieces, one per line. */
  contents?: string;
  /** The description as pre-filled for the product, to spot it left unchanged on an incomplete item. */
  importedDescription?: string;
  locationId: string;
  /** Packing straight into a bin (the lot then takes the bin's location). */
  bin?: { id: string; code: string; locationCode: string | null } | null;
  photos: { key: string; id: string }[];
  primaryPhotoId: string | null;
}

const blank = (keep: Partial<Draft> = {}): Draft => ({
  clientRequestId: crypto.randomUUID(),
  lotNumber: null,
  product: null,
  unlinkedBarcode: null,
  title: '',
  referencePrice: '',
  referenceCurrency: '',
  startingBid: '',
  reservePrice: '',
  bidCurrency: '',
  conditionId: '',
  colour: '',
  sizeModel: '',
  details: '',
  locationId: '',
  photos: [],
  primaryPhotoId: null,
  ...keep,
});

function sourceOf(value: string, a: string | null | undefined, aLabel: string, b: string | null | undefined, bLabel: string) {
  if (!value.trim()) return SOURCE_LABELS.none;
  if (a != null && value.trim() === a) return SOURCE_LABELS[aLabel];
  if (b != null && value.trim() === b) return SOURCE_LABELS[bLabel];
  return SOURCE_LABELS.employee;
}

export function LotCreatePage() {
  const [draft, setDraft, restored] = useStoredState<Draft>(DRAFT_KEY, () => blank());
  const [showRestored, setShowRestored] = useState(restored && !!(draft.lotNumber || draft.product));
  // In-progress uploads live only in memory; finished ones are mirrored into the draft.
  const [uploads, setUploads] = useState<PendingPhoto[]>(() => draft.photos.map((p) => ({ key: p.key, id: p.id, status: 'done' as const })));
  useEffect(() => {
    setDraft((d) => ({ ...d, photos: uploads.filter((p) => p.status === 'done' && p.id).map((p) => ({ key: p.key, id: p.id! })) }));
  }, [uploads, setDraft]);

  // First-time tour (per user), or replayed from Home with ?tour=1.
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [touring, setTouring] = useState(() => params.get('tour') === '1' || (!!user && !user.tourDoneAt));
  const endTour = useCallback(() => {
    setTouring(false);
    if (params.has('tour')) setParams({}, { replace: true });
    if (!user?.tourDoneAt) api('/auth/tour-done', { method: 'POST' }).then(refresh, () => undefined);
  }, [params, setParams, user, refresh]);

  const conditions = useConditions();
  const locations = useLocations();
  const bins = useBins();
  const settings = useSettings();
  const defaultCurrency = settings.data?.defaultCurrency ?? 'CAD';

  const [labelBusy, setLabelBusy] = useState(false);
  const [labelConflict, setLabelConflict] = useState<Lot | null>(null);
  const [labelError, setLabelError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saved, setSaved] = useState<Lot | null>(null);
  const [linkState, setLinkState] = useState<{ busy?: boolean; done?: string; error?: unknown }>({});
  const [locMsg, setLocMsg] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const input = (k: keyof Draft) => ({
    value: (draft[k] as string) ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => set(k, e.target.value as never),
  });

  // Step 1: label
  const scanLabel = async (code: string) => {
    setLabelBusy(true);
    setLabelConflict(null);
    setLabelError(null);
    setSaved(null);
    try {
      const r = await api<{ lotNumber: string; available: boolean; issued: boolean; lot: Lot | null }>(`/lots/check-number/${enc(code)}`);
      if (r.available) set('lotNumber', r.lotNumber);
      else if (r.lot) setLabelConflict(r.lot);
      else setLabelError(new Error(`"${r.lotNumber}" is not a lot label issued by this system. Scan the lot label (not the product's barcode).`));
    } catch (e) {
      setLabelError(e);
    } finally {
      setLabelBusy(false);
    }
  };

  // Step 2: product. Prefill title/price from the product; employee can change or clear them.
  const selectProduct = async (p: { id: string }) => {
    setLinkState({});
    try {
      const [full, pages] = await Promise.all([
        api<{ product: Product }>(`/products/${p.id}`).then((r) => r.product),
        api<{ pages: WebPage[] }>(`/products/${p.id}/web`).then((r) => r.pages),
      ]);
      // Retail: the manifest / master-list price first (what our cost is based on), else the product's reference price.
      const price = full.manifestPrice ?? full.referencePrice;
      const cur = full.manifestPrice ? full.manifestCurrency : full.referenceCurrency;
      // Costco data: the first page (by site priority) gives the title; the first page with photos gives the photo.
      const withPhoto = pages.find((w) => w.images.length > 0);
      const main = withPhoto ?? pages[0];
      const descPage = pages.find((w) => w.description);
      const namePage = pages.find((w) => w.name?.trim());
      setDraft((d) => ({
        ...d,
        product: full,
        // Costco's long title when there is one; otherwise the product's title.
        title: namePage?.name?.trim() ?? productTitle(full) ?? '',
        titlePageId: namePage?.id ?? null,
        referencePrice: price ?? '',
        referenceCurrency: price ? (cur ?? '') : '',
        // The product's own description (if someone set it) wins; otherwise Costco's text as-is.
        costcoDescription: full.description ?? descPage?.description ?? '',
        importedDescription: full.description ?? descPage?.description ?? '',
        costcoPageId: full.description ? null : (descPage?.id ?? null),
        costco: main ? { title: namePage?.name ?? main.name, sourceName: main.sourceName, url: main.url, imageId: withPhoto?.images[0].id ?? null } : null,
        useCostcoPhoto: !!withPhoto,
      }));
    } catch (e) {
      setSaveError(e);
    }
  };

  const linkBarcode = async () => {
    if (!draft.product || !draft.unlinkedBarcode) return;
    setLinkState({ busy: true });
    try {
      await api(`/products/${draft.product.id}/barcodes`, { body: { barcode: draft.unlinkedBarcode } });
      setLinkState({ done: draft.unlinkedBarcode });
      set('unlinkedBarcode', null);
    } catch (e) {
      setLinkState({ error: e });
    }
  };

  // Step 5: location by scan
  const scanLocation = async (code: string) => {
    setLocMsg(null);
    if (isBinCode(code)) return scanBin(code);
    const { status, body } = await apiRaw(`/locations/by-code/${enc(code)}`).catch(() => ({ status: 0, body: null }));
    if (status === 200 && body.location.active) set('locationId', body.location.id);
    else if (status === 200) setLocMsg(`Location ${code} is inactive.`);
    else if (status === 404) setLocMsg(`No location with code "${code}". An administrator can add it in Settings.`);
    else setLocMsg('Could not reach the server to look up that location.');
  };
  const scanBin = async (code: string) => {
    setLocMsg(null);
    const { status, body } = await apiRaw(`/bins/by-code/${enc(code)}`).catch(() => ({ status: 0, body: null }));
    if (status === 200 && body.bin.active) setDraft((d) => ({ ...d, locationId: '', bin: { id: body.bin.id, code: body.bin.code, locationCode: body.bin.locationCode } }));
    else if (status === 200) setLocMsg(`Bin ${body.bin.code} is retired.`);
    else setLocMsg(body?.error?.message ?? 'Could not reach the server to look up that bin.');
  };

  const kind = draft.kind ?? 'product';
  const cd = draft.completeness ?? COMPLETE;
  const incomplete = kind === 'product' && cd.completeness === 'incomplete';
  // On an incomplete item, the imported title / description describe the COMPLETE item and must be changed.
  const imported = (v: string, ...from: (string | null | undefined)[]) => !!v.trim() && from.some((x) => x != null && x.trim() === v.trim());
  const titleImported = incomplete && !!draft.product && imported(draft.title, draft.costco?.title, draft.product.listingTitle, draft.product.manifestTitle);
  const descImported = incomplete && imported(draft.costcoDescription ?? '', draft.importedDescription);
  const blockers = [
    kind === 'product' && draft.product && completenessProblem(cd)?.message,
    titleImported && 'Change the title: it still describes the complete item.',
    descImported && 'Change the Costco description (or clear it): it still describes the complete item.',
    kind === 'mixed' && !draft.contents?.trim() && 'List the pieces in this lot.',
    kind === 'mixed' && !draft.title.trim() && 'Type a title for this mixed lot.',
  ].filter((x): x is string => !!x);

  const uploading = uploads.some((u) => u.status === 'uploading');
  const failedUploads = uploads.some((u) => u.status === 'error');
  const canSave = !!draft.lotNumber && (kind === 'mixed' || !!draft.product) && !blockers.length && !saving && !uploading;

  /** Leave the product: a mixed lot keeps nothing from it (no Costco text, photo or title). */
  const toMixed = (firstPiece = '') =>
    setDraft((d) => ({
      ...d,
      kind: 'mixed',
      product: null,
      unlinkedBarcode: null,
      costco: null,
      costcoDescription: '',
      importedDescription: '',
      costcoPageId: null,
      titlePageId: null,
      useCostcoPhoto: false,
      title: '',
      referencePrice: '',
      referenceCurrency: '',
      completeness: COMPLETE,
      contents: [firstPiece, d.contents ?? ''].filter((x) => x.trim()).join('\n'),
    }));
  const useMixedForThis = () => {
    const name = draft.costco?.title?.trim() || (draft.product ? productTitle(draft.product) : null) || 'Piece';
    const count = cd.piecesPresent.trim() && cd.piecesTotal.trim() ? ` (${cd.piecesPresent.trim()} of ${cd.piecesTotal.trim()} pieces)` : '';
    toMixed(`${name}${count}`);
  };

  const save = async () => {
    if (!canSave) return;
    // Not required, but usually forgotten rather than intended: ask once before saving without them.
    const hasPhoto = uploads.some((u) => u.status === 'done') || (!!draft.useCostcoPhoto && !!draft.costco?.imageId);
    const missing = [
      !draft.conditionId && 'no condition',
      !hasPhoto && 'no photos',
      !draft.details.trim() && 'no inspection notes',
    ].filter(Boolean);
    if (missing.length && !confirm(`This lot has ${missing.join(', ')}. Save anyway?`)) return;
    setSaving(true);
    setSaveError(null);
    try {
      const photoIds = uploads.filter((u) => u.status === 'done' && u.id).map((u) => u.id!);
      const r = await api<{ lot: Lot }>('/lots', {
        body: {
          clientRequestId: draft.clientRequestId,
          lotNumber: draft.lotNumber,
          kind,
          ...(kind === 'mixed' ? { contents: draft.contents?.trim() || null } : { productId: draft.product!.id, ...completenessBody(cd) }),
          title: draft.title || null,
          titlePageId: draft.titlePageId ?? null,
          referencePrice: draft.referencePrice || null,
          referenceCurrency: draft.referencePrice ? draft.referenceCurrency || null : null,
          startingBid: draft.startingBid || null,
          reservePrice: draft.reservePrice || null,
          bidCurrency: draft.startingBid || draft.reservePrice ? draft.bidCurrency || null : null,
          conditionId: draft.conditionId || null,
          colour: draft.colour || null,
          sizeModel: draft.sizeModel || null,
          details: draft.details || null,
          costcoDescription: draft.costcoDescription?.trim() || null,
          costcoDescriptionPageId: draft.costcoPageId ?? null,
          webImageId: draft.useCostcoPhoto && draft.costco?.imageId ? draft.costco.imageId : null,
          locationId: draft.bin ? null : draft.locationId || null,
          binId: draft.bin?.id ?? null,
          photoIds,
          primaryPhotoId: draft.primaryPhotoId && photoIds.includes(draft.primaryPhotoId) ? draft.primaryPhotoId : (photoIds[0] ?? null),
        },
      });
      // Only now, after the server confirmed the save, do we report success and clear the form.
      setSaved(r.lot);
      clearStored(DRAFT_KEY);
      // The next lot usually goes to the same place, so the location / bin is kept.
      setDraft(blank({ locationId: draft.locationId, bin: draft.bin }));
      setUploads([]);
      setLinkState({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'lot_number_assigned') {
        setLabelConflict(e.details?.lot ?? null);
        set('lotNumber', null);
      }
      setSaveError(e);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!confirm('Discard this unsaved lot?')) return;
    uploads.forEach((u) => u.id && api(`/photos/pending/${u.id}`, { method: 'DELETE' }).catch(() => undefined));
    clearStored(DRAFT_KEY);
    setDraft(blank({ locationId: draft.locationId, bin: draft.bin }));
    setUploads([]);
    setShowRestored(false);
    setSaveError(null);
    setLabelConflict(null);
  };

  const p = draft.product;
  const selectedLocation = locations.data?.find((l) => l.id === draft.locationId);

  return (
    <div className="narrow">
      <h1>Create lot</h1>
      {saved && (
        <Success>
          Lot <strong className="mono">{saved.lotNumber}</strong> saved{saved.bin ? ` in ${saved.bin.code}` : ''}
          {saved.location ? ` at ${saved.location.code}` : ''} with {saved.photos.length} photo(s).{' '}
          <Link to={`/lots/${saved.id}`}>View lot</Link>
        </Success>
      )}
      {showRestored && (
        <div className="alert alert-info">
          Restored your unsaved lot from earlier.{' '}
          <button className="btn btn-small" onClick={discard}>
            Discard it
          </button>
        </div>
      )}

      <section className="card step" data-tour="product">
        <StepHeading tip="product">1. Product</StepHeading>
        <div className="seg">
          <button type="button" className={`btn btn-small ${kind === 'product' ? 'on' : ''}`} onClick={() => set('kind', 'product')}>
            One product
          </button>
          <button
            type="button"
            className={`btn btn-small ${kind === 'mixed' ? 'on' : ''}`}
            onClick={() => kind !== 'mixed' && (!p || confirm('Leave this product? A mixed lot is not attached to any product, so its Costco title, description and photo are removed.')) && toMixed()}
          >
            Mixed / parts lot
          </button>
        </div>
        {kind === 'mixed' ? (
          <div>
            <Field
              label="Pieces in this lot (one per line)"
              hint="Not attached to any product. Use this for pieces from different items, or when half or less of an item is here."
            >
              <textarea
                value={draft.contents ?? ''}
                onChange={(e) => set('contents', e.target.value)}
                rows={4}
                maxLength={5000}
                placeholder={'1 × saucepan with lid (from 3-pc set)\n1 × 28 cm frying pan (from 11-pc set)'}
              />
            </Field>
          </div>
        ) : p ? (
          <div>
            <div className="product-confirm">
              {draft.costco?.imageId ? (
                <img src={webImageUrl(draft.costco.imageId)} alt="Costco photo of this item" onClick={() => setViewing(draft.costco!.imageId)} />
              ) : (
                <Thumb id={null} size={110} />
              )}
              <div>
                <div className="chosen">
                  <span className="mono">{p.itemNumber}</span> <Val v={productTitle(p)} missing="no title" />
                </div>
                {draft.costco?.title && (
                  <div className="small">
                    {draft.costco.sourceName}:{' '}
                    <a href={draft.costco.url} target="_blank" rel="noreferrer">
                      {draft.costco.title}
                    </a>
                  </div>
                )}
                {!draft.costco && <div className="muted small">No Costco website information for this item.</div>}
                <p className="small">Check the photo matches the item in front of you.</p>
                <button
                  className="btn btn-small btn-ghost"
                  // A different product must not keep the previous product's Costco text or photo.
                  onClick={() =>
                    setDraft((d) => ({ ...d, product: null, costco: null, costcoDescription: '', importedDescription: '', costcoPageId: null, titlePageId: null, useCostcoPhoto: false, completeness: COMPLETE }))
                  }
                >
                  Change product
                </button>
              </div>
            </div>
            <div className="muted small">
              Imported: <Val v={p.manifestTitle} missing="no description" /> · retail <Money amount={p.manifestPrice} currency={p.manifestCurrency} /> · sell{' '}
              <Money amount={p.manifestSellPrice ?? null} currency={p.manifestCurrency} />
            </div>
            {draft.unlinkedBarcode && (
              <div className="alert alert-info">
                Barcode <span className="mono">{draft.unlinkedBarcode}</span> is not linked to any product. Link it to item <span className="mono">{p.itemNumber}</span>? Only do this if
                the barcode is on this product.{' '}
                <button className="btn btn-small" disabled={linkState.busy} onClick={linkBarcode}>
                  {linkState.busy ? 'Linking…' : 'Link barcode'}
                </button>{' '}
                <button className="btn btn-small btn-ghost" onClick={() => set('unlinkedBarcode', null)}>
                  No
                </button>
              </div>
            )}
            {linkState.done && <Success>Barcode {linkState.done} linked to this product.</Success>}
            <ErrorBox error={linkState.error} />
            <CompletenessFields value={cd} onChange={(v) => set('completeness', v)} onUseMixed={useMixedForThis} />
          </div>
        ) : (
          <ProductPicker onSelect={selectProduct} onUnknownBarcode={(c) => set('unlinkedBarcode', c)} />
        )}
      </section>

      <section className="card step" data-tour="photos">
        <StepHeading tip="photos">2. Photos of this item</StepHeading>
        {draft.costco?.imageId && (
          <div className="row gap">
            <div className="photo-cell" style={{ width: 130 }}>
              <span className="photo-badge">Costco</span>
              <img src={webImageUrl(draft.costco.imageId)} alt="" onClick={() => setViewing(draft.costco!.imageId)} />
            </div>
            <label className="check">
              <input type="checkbox" checked={!!draft.useCostcoPhoto} onChange={(e) => set('useCostcoPhoto', e.target.checked)} /> Use the Costco photo as the first (main) lot
              photo. Your photos follow it.
            </label>
          </div>
        )}
        <PendingPhotos photos={uploads} setPhotos={setUploads} primary={draft.primaryPhotoId} setPrimary={(id) => set('primaryPhotoId', id)} />
        {failedUploads && <div className="alert alert-warn">Some photos failed to upload. Retry them, or remove them before saving.</div>}
      </section>

      <section className="card step" data-tour="details">
        <StepHeading tip="details">3. Details</StepHeading>
        <div className={titleImported ? 'must-change' : ''}>
          <Field
            label="Listing title for this lot"
            hint={
              kind === 'mixed'
                ? 'Required: say what this bundle is.'
                : !p
                  ? undefined
                  : titleImported
                    ? 'Imported for the complete item: you must change this.'
                    : incomplete && draft.title.trim()
                      ? 'Typed by you, for this lot only (not kept for the next lot of this item).'
                      : draft.costco?.title && draft.title.trim() === draft.costco.title.trim()
                        ? `From ${draft.costco.sourceName}`
                        : sourceOf(draft.title, p.listingTitle, 'product_listing_title', p.manifestTitle, 'product_manifest_title') === SOURCE_LABELS.employee && !p.listingTitle
                          ? 'Typed by you. It will also be saved as this item\'s title, so the next lot of it starts with it.'
                          : sourceOf(draft.title, p.listingTitle, 'product_listing_title', p.manifestTitle, 'product_manifest_title')
            }
          >
            <input {...input('title')} maxLength={500} />
          </Field>
          {titleImported && (
            <button type="button" className="btn btn-small mb" onClick={() => set('title', `INCOMPLETE – ${draft.title.trim()}`)}>
              Add "INCOMPLETE –" to the title
            </button>
          )}
        </div>
        <div className="grid2">
          <Field label="Condition">
            <select {...input('conditionId')}>
              <option value="">— not set —</option>
              {conditions.data?.filter((c) => c.active).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Colour">
            <input {...input('colour')} maxLength={100} />
          </Field>
        </div>
        {conditions.data?.length === 0 && <div className="muted small">No condition options yet. An administrator can add them in Settings.</div>}
        <Field label="Size / model (optional)">
          <input {...input('sizeModel')} maxLength={200} />
        </Field>
        <Field label="Lot inspection (what you checked)" hint="What you can see: contents, defects, missing parts. Only facts you checked.">
          <textarea {...input('details')} rows={3} maxLength={5000} />
        </Field>
        {kind === 'product' && (
          <div className={descImported ? 'must-change' : ''}>
            <Field
              label="Costco description"
              hint={
                descImported
                  ? 'Imported for the complete item: you must change this (or clear it).'
                  : !draft.costcoDescription?.trim()
                    ? 'None found for this item. Leave blank, or type one.'
                    : draft.costcoPageId
                      ? `From ${draft.costco?.sourceName ?? 'the Costco website'}. Edit if needed (it will then show as edited by you).`
                      : "From the product's description."
              }
            >
              <textarea value={draft.costcoDescription ?? ''} onChange={(e) => set('costcoDescription', e.target.value)} rows={5} maxLength={10000} />
            </Field>
            {descImported && (
              <button
                type="button"
                className="btn btn-small mb"
                disabled={!cd.missingParts.trim()}
                title={cd.missingParts.trim() ? undefined : "Fill in what's missing (step 1) first"}
                onClick={() => set('costcoDescription', `Missing: ${cd.missingParts.trim()}\n\n${draft.costcoDescription ?? ''}`)}
              >
                Add "Missing: …" to the top
              </button>
            )}
          </div>
        )}
        <div className="grid2">
          <Field
            label="Retail price"
            hint={
              !p
                ? 'Leave blank if unknown'
                : incomplete && sourceOf(draft.referencePrice, p.referencePrice, 'product_reference_price', p.manifestPrice, 'product_manifest_price') !== SOURCE_LABELS.employee && draft.referencePrice.trim()
                  ? 'This is the price of the complete item. Change it if you want.'
                  : sourceOf(draft.referencePrice, p.referencePrice, 'product_reference_price', p.manifestPrice, 'product_manifest_price')
            }
          >
            <input {...input('referencePrice')} inputMode="decimal" placeholder="blank = unknown" />
          </Field>
          <Field label="Currency">
            <select {...input('referenceCurrency')}>
              <option value="">—</option>
              {[...new Set([defaultCurrency, 'CAD', 'USD', draft.referenceCurrency].filter(Boolean))].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
        </div>
        {p && <CostLine category={p.mainCategory ?? null} subcategory={p.realSubcategory ?? null} retail={draft.referencePrice} currency={draft.referenceCurrency} percents={settings.data?.costPercent} />}
        <details>
          <summary>Starting bid / reserve (optional)</summary>
          <div className="grid3">
            <Field label="Starting bid">
              <input {...input('startingBid')} inputMode="decimal" />
            </Field>
            <Field label="Reserve">
              <input {...input('reservePrice')} inputMode="decimal" />
            </Field>
            <Field label="Currency">
              <select {...input('bidCurrency')}>
                <option value="">—</option>
                {[...new Set([defaultCurrency, 'CAD', 'USD'])].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>
        </details>
      </section>

      <section className="card step" data-tour="storage">
        <StepHeading tip="storage">4. Storage location or bin</StepHeading>
        {draft.bin ? (
          <div className="chosen">
            <span className="bin-chip">🧺 {draft.bin.code}</span> <span className="muted small">at {draft.bin.locationCode ?? 'no location'}</span>
            <button className="btn btn-small btn-ghost" onClick={() => set('bin', null)}>
              Change
            </button>
          </div>
        ) : selectedLocation ? (
          <div className="chosen">
            <span className="loc-chip">📍 {selectedLocation.code}</span> <span className="muted small">{locationText(selectedLocation)}</span>
            <button className="btn btn-small btn-ghost" onClick={() => set('locationId', '')}>
              Change
            </button>
          </div>
        ) : (
          <>
            <ScanField kind="location" label="LOCATION OR BIN" onScan={scanLocation} />
            {locMsg && <div className="alert alert-warn">{locMsg}</div>}
            {locations.isLoading ? (
              <Loading />
            ) : (
              <Field label="…or choose">
                <select
                  value={draft.locationId}
                  onChange={(e) => {
                    const v = e.target.value;
                    const b = v.startsWith('bin:') ? bins.data?.find((x) => x.id === v.slice(4)) : null;
                    if (b) setDraft((d) => ({ ...d, locationId: '', bin: { id: b.id, code: b.code, locationCode: b.locationCode } }));
                    else set('locationId', v);
                  }}
                >
                  <option value="">— no location yet —</option>
                  {!!bins.data?.length && (
                    <optgroup label="Bins (lot goes into the bin)">
                      {bins.data.map((b) => (
                        <option key={b.id} value={`bin:${b.id}`}>
                          🧺 {b.code}
                          {b.locationCode ? ` (at ${b.locationCode})` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Locations">
                    {locations.data?.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.code}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </Field>
            )}
          </>
        )}
      </section>

      <section className="card step" data-tour="label">
        <StepHeading tip="label">5. Lot label</StepHeading>
        {draft.lotNumber ? (
          <div className="chosen">
            <span className="lot-chip mono">🏷️ {draft.lotNumber}</span>
            <span className="ok">available</span>
            <button className="btn btn-small btn-ghost" onClick={() => set('lotNumber', null)}>
              Change
            </button>
          </div>
        ) : (
          // Last step. Not focused on its own: that would jump the page past the photos.
          <ScanField kind="lot" onScan={scanLabel} busy={labelBusy} />
        )}
        <ErrorBox error={labelError} />
        {labelConflict && (
          <div className="alert alert-error">
            Label <strong className="mono">{labelConflict.lotNumber}</strong> is already assigned. It will not be reused or overwritten.
            <div className="mt">
              {labelConflict.title ?? <span className="missing">no title</span>} · {labelConflict.productItemNumber ? `item ${labelConflict.productItemNumber}` : 'mixed lot'} · location{' '}
              <Val v={labelConflict.location?.code} missing="none" />{' '}
              <Link to={`/lots/${labelConflict.id}`}>Open that lot</Link>
            </div>
          </div>
        )}
      </section>

      {viewing && (
        <Modal title="Costco photo" onClose={() => setViewing(null)}>
          <img src={webImageUrl(viewing, 'full')} alt="" className="photo-full" />
        </Modal>
      )}

      {touring && <Tour steps={CREATE_LOT_TOUR} onClose={endTour} />}

      <div className="savebar" data-tour="save">
        <ErrorBox error={saveError} />
        {kind === 'product' && !p && <div className="muted small">Select a product, or choose "Mixed / parts lot".</div>}
        {!draft.lotNumber && (kind === 'mixed' || p) && <div className="muted small">Scan the lot label (step 5) to save.</div>}
        {blockers.map((b) => (
          <div key={b} className="small field-error">
            {b}
          </div>
        ))}
        {uploading && <div className="muted small">Waiting for photos to finish uploading…</div>}
        <div className="row gap">
          <button className="btn btn-primary btn-big" disabled={!canSave} onClick={save}>
            {saving ? 'Saving…' : 'Save lot'}
          </button>
          <button className="btn btn-ghost" onClick={discard} disabled={saving}>
            Discard
          </button>
        </div>
      </div>
    </div>
  );
}

/** The product's category, and the cost the lot will get (estimated from retail x the category's %). */
function CostLine({
  category,
  subcategory,
  retail,
  currency,
  percents,
}: {
  category: MainCategory | null;
  subcategory: string | null;
  retail: string;
  currency: string;
  percents: Record<MainCategory, number> | undefined;
}) {
  const est = estimatedCost(retail, category, percents);
  return (
    <div className="muted small">
      Category: <Val v={categoryText(category, subcategory)} missing="none yet" />
      {' · '}
      {est ? (
        <>
          Estimated cost <Money amount={est.cost} currency={currency || null} /> ({est.percent}% of retail)
        </>
      ) : (
        <>No cost estimate ({category ? 'no retail price' : 'no category'})</>
      )}
    </div>
  );
}
